<script setup lang="ts">
import { InputBox, PopupBasicHeader } from 'wepin-vue-component'
import {
  BasicBottomButtons as BBB,
  WrappedNetworkIcon,
  NoticeText,
} from '@/components'
import {
  useAccountQuery,
  useBreakPointsClass,
  useI18nTypes,
  useInput,
  useKeyDown,
} from '@/composables'
import { defaultBreakPoints } from '@/const'
import { useLoadingsStore } from '@/stores'

const emits = defineEmits<{
  (event: 'close'): void
}>()

const { t } = useI18nTypes()
const loadingsStore = useLoadingsStore()
const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const { account } = useAccountQuery()
const { isError, inputValue: label, onInputLabel } = useInput()
const onClickConfirm = async () => {
  if (label.value.length === 0 || isError.value) return
  loadingsStore.openSpinner()
  try {
    await account.value!.setLabel(label.value)
    emits('close')
  } catch (error) {
    // Error handled silently
  } finally {
    loadingsStore.closeSpinner()
  }
}
const onClickCancel = () => {
  emits('close')
}

useKeyDown({ confirm: onClickConfirm })
</script>

<template>
  <div v-if="account" class="change-label" :class="breakPointsClass">
    <PopupBasicHeader :title="t('change_label.title')" :exit="onClickCancel" />
    <div class="popup-body">
      <div class="header">
        <WrappedNetworkIcon
          class="network-icon"
          :token-src="account.coin?.iconUrl"
          :network-src="account.coin?.coin?.iconUrl"
        />
        <div class="coin-name">
          {{ account.coin?.name }}
        </div>
        <div class="coin-symbol">
          {{ account.coin?.symbol }}
        </div>
      </div>
      <InputBox
        width="100%"
        class="label-input"
        :placeholder="t('create_account.name_placeholder')"
        :clear="true"
        :model-value="label"
        :error="isError"
        maxlength="14"
        @update:model-value="onInputLabel"
      />
      <NoticeText class="notice-wrapper" :is-error="isError">
        {{ t('create_account.input_name_caution') }}
      </NoticeText>
      <BBB
        :disabled="label.length === 0 || isError"
        @confirm="onClickConfirm"
        @cancel="onClickCancel"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.change-label {
  width: 460px;
  background-color: var(--grey_fff);
  border-radius: 8px;
  box-shadow: 0px 20px 40px rgba(0, 0, 0, 0.2);
  & > .popup-body {
    padding: {
      left: 30px;
      right: 30px;
      bottom: 30px;
    }
    & > .header {
      display: flex;
      align-items: center;
      & > .coin-name {
        @include typeface('Title_18');
        margin-left: 15px;
      }
      & > .coin-symbol {
        @include typeface('Paragraph_16_r');
        color: var(--grey_999);
        margin-left: 8px;
      }
    }
    & > .label-input {
      margin-top: 20px;
    }
    & > .notice-caution {
      margin-top: 10px;
    }
  }
}

.notice-wrapper {
  background-color: transparent;
  margin-top: 10px;
  margin-bottom: 20px;
  padding: initial;
}

.change-label.mobile {
  width: 100%;
  & > .popup-body {
    padding: {
      left: 20px;
      right: 20px;
    }
  }
}
</style>
